// =============================================================================
// Content mix for "Plan my month" (spec §6 flow 5, step 2).
//
// The owner says roughly what the month should feel like — "mostly selling" or
// "mostly community" — as percentages, and this turns that into an actual list
// of post roles.
//
// Percentages are a lie that has to be resolved honestly: 7 posts at
// 40/30/20/10 gives 2.8/2.1/1.4/0.7 posts. Rounding each independently loses or
// gains posts, so the allocation below uses largest-remainder — the same method
// used for apportioning seats — which always sums to exactly the requested
// count.
// =============================================================================
/** The buckets a month is divided into. */
export const MIX_BUCKETS = ["promo", "tips", "behind_scenes", "engagement"];
export const MIX_PRESETS = [
    {
        key: "balanced",
        labelKey: "planMonth.presets.balanced.label",
        descriptionKey: "planMonth.presets.balanced.description",
        // The spec's own example.
        mix: { promo: 40, tips: 30, behind_scenes: 20, engagement: 10 },
    },
    {
        key: "sales_focused",
        labelKey: "planMonth.presets.salesFocused.label",
        descriptionKey: "planMonth.presets.salesFocused.description",
        mix: { promo: 65, tips: 15, behind_scenes: 10, engagement: 10 },
    },
    {
        key: "community_focused",
        labelKey: "planMonth.presets.communityFocused.label",
        descriptionKey: "planMonth.presets.communityFocused.description",
        mix: { promo: 20, tips: 30, behind_scenes: 25, engagement: 25 },
    },
];
export function mixPresetFor(key) {
    const preset = MIX_PRESETS.find((p) => p.key === key);
    if (!preset)
        throw new Error(`Unknown content mix preset: ${key}`);
    return preset;
}
/** The post role each bucket generates. */
const BUCKET_ROLE = {
    promo: "announcement",
    tips: "tip",
    behind_scenes: "behind_scenes",
    engagement: "engagement",
};
/** What the generator needs for each bucket. */
export const BUCKET_GUIDANCE = {
    promo: "Promote a product, offer or service. Give a clear reason to buy now and one " +
        "specific call to action.",
    tips: "Teach something genuinely useful about the product category, with no sell. " +
        "The reader should be able to act on it today.",
    behind_scenes: "Show the people or the process behind the business — making, packing, " +
        "sourcing, a normal working day. Warm and specific, not corporate.",
    engagement: "Ask a question or invite a reply. One short, easy question that does not " +
        "require the reader to know anything in advance.",
};
/**
 * Turns percentages into exactly `postCount` entries.
 *
 * Largest-remainder: floor every share, then hand the leftover posts to the
 * buckets with the biggest fractional parts. The total always matches what the
 * owner asked for, which matters because that number is what they are billed
 * against (spec §9).
 *
 * Percentages need not sum to 100 — the sliders in the UI can drift — so they
 * are normalised by their actual total rather than trusted.
 */
export function allocateMix(mix, postCount) {
    if (postCount <= 0)
        return [];
    const total = MIX_BUCKETS.reduce((sum, b) => sum + Math.max(0, mix[b] ?? 0), 0);
    // Every bucket zeroed: fall back to promo rather than returning nothing,
    // since the owner did ask for posts.
    if (total <= 0) {
        return Array.from({ length: postCount }, () => ({
            bucket: "promo",
            role: BUCKET_ROLE.promo,
            guidance: BUCKET_GUIDANCE.promo,
        }));
    }
    const exact = MIX_BUCKETS.map((bucket) => ({
        bucket,
        share: (Math.max(0, mix[bucket] ?? 0) / total) * postCount,
    }));
    const counts = new Map(exact.map((e) => [e.bucket, Math.floor(e.share)]));
    let assigned = [...counts.values()].reduce((a, b) => a + b, 0);
    const byRemainder = [...exact]
        .map((e) => ({ bucket: e.bucket, remainder: e.share - Math.floor(e.share) }))
        .sort((a, b) => b.remainder - a.remainder);
    let i = 0;
    while (assigned < postCount) {
        const bucket = byRemainder[i % byRemainder.length].bucket;
        counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
        assigned++;
        i++;
    }
    // Interleave rather than grouping: emitting all the promos first would give
    // the owner a week of selling followed by a week of tips.
    return interleave(counts);
}
/**
 * Spreads the allocations so the same bucket does not run consecutively.
 *
 * Round-robin weighted by remaining count, so a 65% promo month still alternates
 * instead of producing a block of promos and then everything else.
 */
function interleave(counts) {
    const remaining = new Map(counts);
    const out = [];
    let lastBucket = null;
    const totalRemaining = () => [...remaining.values()].reduce((a, b) => a + b, 0);
    while (totalRemaining() > 0) {
        // Largest remaining count first, but never the same bucket twice in a row
        // unless it is all that is left.
        const candidates = [...remaining.entries()]
            .filter(([, n]) => n > 0)
            .sort((a, b) => b[1] - a[1]);
        const pick = candidates.find(([bucket]) => bucket !== lastBucket) ?? candidates[0];
        const [bucket] = pick;
        remaining.set(bucket, (remaining.get(bucket) ?? 0) - 1);
        lastBucket = bucket;
        out.push({
            bucket,
            role: BUCKET_ROLE[bucket],
            guidance: BUCKET_GUIDANCE[bucket],
        });
    }
    return out;
}
/** First day of a month, as a YYYY-MM-DD key. */
export function monthKey(year, month) {
    return `${year}-${String(month).padStart(2, "0")}-01`;
}
/** Days in a month — used to bound the post count the UI offers. */
export function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
//# sourceMappingURL=contentMix.js.map